/**
 * Shared utilities for Featured Articles components
 * Used by both AJAX filter and tabs functionality
 */

if (!window.FeaturedArticlesUtils) {
  window.FeaturedArticlesUtils = {
    /**
     * Extract article data from a blog article card element
     */
    extractArticleData(articleCard) {
      const articleLink = articleCard.querySelector('a.full-unstyled-link, a.full-link');
      const articleImage = articleCard.querySelector('img');
      const articleTitle = articleCard.querySelector('h2, h3');
      const articleExcerpt = articleCard.querySelector('.rte');
      const articleDate = articleCard.querySelector('time');
      const articleBadge = articleCard.querySelector('.article-card__badge, .badge');

      if (!articleLink || !articleTitle) {
        return null;
      }

      return {
        url: articleLink.href,
        title: articleTitle.textContent.trim(),
        image: articleImage ? {
          src: articleImage.src,
          alt: articleImage.alt || articleTitle.textContent.trim(),
          width: parseInt(articleImage.width) || parseInt(articleImage.getAttribute('width')) || 533,
          height: parseInt(articleImage.height) || parseInt(articleImage.getAttribute('height')) || 300
        } : null,
        excerpt: articleExcerpt ? articleExcerpt.textContent.trim() : '',
        date: articleDate ? articleDate.getAttribute('datetime') || articleDate.textContent.trim() : null,
        badge: articleBadge ? articleBadge.textContent.trim() : null
      };
    },

    /**
     * Detect image height class from existing cards
     */
    detectImageHeightClass(parentElement) {
      const existingImage = parentElement.querySelector('.featured-articles__image');

      if (existingImage) {
        const classes = existingImage.className.split(' ');

        // Check for adapt mode first
        if (classes.includes('media--adapt')) {
          return 'adapt';
        }

        // Look for article-card__image--{size} class
        const imageClass = classes.find(c => c.startsWith('article-card__image--'));
        if (imageClass) {
          return imageClass;
        }
      }

      // Fallback to adapt if no existing card found
      return 'adapt';
    },

    /**
     * Re-initialize slider component after DOM changes
     */
    reinitSlider(parentElement) {
      const sliderComponent = parentElement.closest('slider-component');
      if (!sliderComponent) return;

      // Use resetPages() to re-query slider items and recalculate pages
      if (typeof sliderComponent.resetPages === 'function') {
        // Wait a tick for DOM to settle after inserting new elements
        setTimeout(() => {
          sliderComponent.resetPages();
          // Update button visibility after reinit
          this.updateSliderButtonsVisibility(sliderComponent);
        }, 50);
      }
    },

    /**
     * Show or hide slider control buttons based on whether scrolling is needed
     */
    updateSliderButtonsVisibility(sliderComponent) {
      if (!sliderComponent) return;

      const slider = sliderComponent.querySelector('[id^="Slider-"]');
      const sliderItems = sliderComponent.querySelectorAll('[id^="Slide-"]:not(.featured-articles__ajax-placeholder):not(.featured-articles-tabs__lazy-placeholder)');

      // Find button container (could be inside or outside slider component)
      let buttonContainer = sliderComponent.querySelector('.slider-buttons');
      if (!buttonContainer) {
        // Look for external buttons
        const section = sliderComponent.closest('[id^="shopify-section"]') || sliderComponent.closest('.featured-articles, .featured-articles-tabs, [class*="metaobject-featured-articles"]');
        if (section) {
          buttonContainer = section.querySelector('.slider-buttons');
        }
      }

      if (!buttonContainer || !slider) return;

      // Get visible items only (exclude placeholders and items with 0 width)
      const visibleItems = Array.from(sliderItems).filter(item => {
        return item.clientWidth > 0 && !item.classList.contains('featured-articles__ajax-placeholder') && !item.classList.contains('featured-articles-tabs__lazy-placeholder');
      });

      // Calculate if scrolling is needed
      const sliderWidth = slider.clientWidth;
      let totalItemsWidth = 0;
      visibleItems.forEach(item => {
        totalItemsWidth += item.offsetWidth;
      });

      // Hide buttons if no scrolling needed or no items
      if (visibleItems.length === 0 || totalItemsWidth <= sliderWidth) {
        buttonContainer.style.display = 'none';
      } else {
        buttonContainer.style.display = '';
      }
    },

    /**
     * Hide slider buttons - wrapper for updateSliderButtonsVisibility
     */
    hideSliderButtons(ulContainer) {
      const sliderComponent = ulContainer.closest('slider-component');
      if (sliderComponent) {
        this.updateSliderButtonsVisibility(sliderComponent);
      }
    },

    /**
     * Trigger animation for newly added items
     */
    triggerAnimation(parentElement) {
      const animateList = parentElement.closest('appear-animate-list');
      if (!animateList) {
        // Fallback: add done class immediately if no animation container
        const newItems = parentElement.querySelectorAll('.featured-articles__item:not(.done)');
        newItems.forEach(item => item.classList.add('done'));
        return;
      }

      // Add 'done' class to newly inserted items to trigger animation
      const newItems = parentElement.querySelectorAll('.featured-articles__item:not(.done)');
      newItems.forEach((item, index) => {
        setTimeout(() => {
          if (item.isConnected) {
            item.classList.add('done');
          }
        }, index * 170); // 170ms delay between each item
      });
    },

    /**
     * Format date for display
     */
    formatDate(dateString) {
      if (!dateString) return '';

      if (dateString.includes('T')) {
        const dateObj = new Date(dateString);
        // Validate date is valid before formatting
        if (!isNaN(dateObj.getTime())) {
          return dateObj.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          });
        }
      }

      return dateString;
    },

    /**
     * Escape HTML to prevent XSS
     */
    escapeHtml(text) {
      if (text === null || text === undefined) {
        return '';
      }
      const div = document.createElement('div');
      div.textContent = String(text);
      return div.innerHTML;
    },

    /**
     * Create article card HTML
     * @param {Object} data - Article data
     * @param {Object} options - Card options
     * @returns {string} HTML string
     */
    createArticleCardHTML(data, options = {}) {
      const {
        isLargeCard = false,
        imageHeight = 'medium',
        showCategoryBadge = true,
        categoryBadgeBg = '#f7d060',
        categoryBadgeText = '#1a1a1a',
        showDatePrefix = false
      } = options;

      let cardHTML = `
      <div class="featured-articles__card${isLargeCard ? ' featured-articles__card--large' : ' featured-articles__card--small'} card-wrapper por">
    `;

      // Add image if exists
      if (data.image) {
        // Determine image classes and inline style based on height setting
        let imageClasses = 'featured-articles__image media media--hover-effect ratio';
        let imageStyle = '';

        if (imageHeight === 'adapt') {
          imageClasses += ' media--adapt';
          // Calculate aspect ratio for adapt mode
          if (data.image.width && data.image.height) {
            const aspectRatio = (data.image.height / data.image.width) * 100;
            imageStyle = ` style="padding-bottom: ${aspectRatio}%;"`;
          }
        } else {
          imageClasses += ` article-card__image--${imageHeight}`;
        }

        cardHTML += `
        <div class="featured-articles__image-wrapper">
          <use-animate class="${imageClasses}"${imageStyle}>
            <img src="${this.escapeHtml(data.image.src)}"
                 alt="${this.escapeHtml(data.image.alt)}"
                 loading="lazy"
                 width="${data.image.width}"
                 height="${data.image.height}">
          </use-animate>`;

        // Add badge if exists and enabled
        if (data.badge && showCategoryBadge) {
          cardHTML += `
          <div class="featured-articles__badge featured-articles__badge--category"
               style="background-color: ${this.escapeHtml(categoryBadgeBg)}; color: ${this.escapeHtml(categoryBadgeText)};">
            ${this.escapeHtml(data.badge)}
          </div>`;
        }

        cardHTML += `</div>`;
      }

      // Add content
      cardHTML += `
      <div class="featured-articles__content">
        <h3 class="featured-articles__card-title h4 margin0">
          <a href="${this.escapeHtml(data.url)}" class="full-unstyled-link">
            ${this.escapeHtml(data.title)}
          </a>
        </h3>`;

      if (data.excerpt) {
        cardHTML += `
        <p class="featured-articles__card-description">
          ${this.escapeHtml(data.excerpt)}
        </p>`;
      }

      if (data.date) {
        const formattedDate = this.formatDate(data.date);
        const datePrefix = showDatePrefix ? 'Last modified: ' : '';

        cardHTML += `
        <p class="featured-articles__card-date caption-large light">
          ${datePrefix}${this.escapeHtml(formattedDate)}
        </p>`;
      }

      cardHTML += `
        <a href="${this.escapeHtml(data.url)}" class="featured-articles__full-link" aria-label="${this.escapeHtml(data.title)}">
          <span class="visually-hidden">${this.escapeHtml(data.title)}</span>
        </a>
      </div>
    </div>`;

      return cardHTML;
    }
  };
}
